import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import SessionNotePDF from "./SessionNotePDF";
import { saveAs } from 'file-saver';
import { pdf } from "@react-pdf/renderer";
import { getUserSession } from "../../services/ActivePrograms";
import moment from "moment";

const ChartCapture = ({ setDownloadPDF, downloadPDF, state, member }) => {
  const chartRef1 = useRef(null);
  const chartRef2 = useRef(null);
  const chartRef3 = useRef(null);
  const chartRef4 = useRef(null);
  const chartRef5 = useRef(null);
  const [session, setSession] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const getSession = async () => {
    try {
      const res = await getUserSession(
        state?._id,
        state?.programId,
        member?.user_id
      );
      setSession(res?.data?.data);
    } catch (error) {
      console.error("Error fetching session data:", error);
    }
  };

  useEffect(() => {
    if (downloadPDF) {
      getSession();
    }
  }, [downloadPDF]);

  const generatePDF = async (imageData1, imageData2, imageData3, imageData4, imageData5) => {
    try {
      setIsGenerating(true);
      const blob = await pdf(
        <SessionNotePDF 
          chartImage1={imageData1} 
          chartImage2={imageData2} 
          chartImage3={imageData3} 
          chartImage4={imageData4} 
          chartImage5={imageData5} 
          state={state}
          member={member}
          session={session}
        />
      ).toBlob();
      
      const fileName = `${member?.userName || 'session'}_note.pdf`;
      saveAs(blob, fileName);
      setDownloadPDF(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!downloadPDF || !session || isGenerating) return;

    const ctx1 = chartRef1.current.getContext("2d");
    const ctx2 = chartRef2.current.getContext("2d");
    const ctx3 = chartRef3.current.getContext("2d");
    const ctx4 = chartRef4.current.getContext("2d");
    const ctx5 = chartRef5.current.getContext("2d");
   
    const bloodPressureData = session?.vitals?.map(item => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      systolic: item?.bloodBP ? parseInt(item?.bloodBP.toString().split("/")[0]) : 0,
      diastolic: item?.bloodBP ? parseInt(item?.bloodBP.toString().split("/")[1]) : 0
    })) || [];

    const respiratoryData = session?.vitals?.map(item => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      value: item?.respRate ? parseInt(item?.respRate) : 0
    })) || [];

    const temperatureData = session?.vitals?.map(item => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      value: item?.bodyTemp ? parseInt(item?.bodyTemp) : 0
    })) || [];

    const oxygenData = session?.vitals?.map(item => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      value: item?.bloodO2 ? parseInt(item?.bloodO2) : 0
    })) || [];

    const heartRateData = session?.vitals?.map(item => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      value: item?.heartRate ? parseInt(item?.heartRate) : 0
    })) || [];

    const chart1 = new Chart(ctx1, {
      type: "line",
      data: {
        labels: respiratoryData.map(item => item.date),
        datasets: [
          {
            label: "Systolic",
            data: bloodPressureData.map(item => item.systolic),
            backgroundColor: "rgba(0, 200, 180, 0.2)",
            borderColor: "rgba(0, 200, 180, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
          {
            label: "Diastolic",
            data: bloodPressureData.map(item => item.diastolic),
            backgroundColor: "rgba(100, 100, 100, 0.2)",
            borderColor: "rgba(100, 100, 100, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            // min: 0,
            // max: 200,
            ticks: {
              // stepSize: 40,
              font: {
                size: 35,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 35,
              },
            },
          },
        },
      },
    });

    const chart2 = new Chart(ctx2, {
      type: "line",
      data: {
        labels: respiratoryData.map(item => item.date),
        datasets: [
          {
            label: "Respiratory Rate",
            data: respiratoryData.map(item => item.value),
            backgroundColor: "rgba(0, 200, 180, 0.2)",
            borderColor: "rgba(0, 200, 180, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            // min: 0,
            // max: 200,
            ticks: {
              //  stepSize: 40,
              font: {
                size: 35,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 35,
              },
            },
          },
        },
      },
    });

    const chart3 = new Chart(ctx3, {
      type: "line",
      data: {
        labels: temperatureData.map(item => item.date),
        datasets: [
          {
            label: "Temperature",
            data: temperatureData.map(item => item.value),
            backgroundColor: "rgba(0, 200, 180, 0.2)",
            borderColor: "rgba(0, 200, 180, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            // min: 0,
            // max: 200,
            ticks: {
              // stepSize: 40,
              font: {
                size: 35,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 35,
              },
            },
          },
        },
      },
    });

    const chart4 = new Chart(ctx4, {
      type: "line",
      data: {
        labels: oxygenData.map(item => item.date),
        datasets: [
          {
            label: "Oxygen Saturation",
            data: oxygenData.map(item => item.value),
            backgroundColor: "rgba(0, 200, 180, 0.2)",
            borderColor: "rgba(0, 200, 180, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            // min: 0,
            // max: 200,
            ticks: {
              // stepSize: 40,
              font: {
                size: 35,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 35,
              },
            },
          },
        },
      },
    });

    const chart5 = new Chart(ctx5, {
      type: "line",
      data: {
        labels: heartRateData.map(item => item.date),
        datasets: [
          {
            label: "Heart Rate",
            data: heartRateData.map(item => item.value),
            backgroundColor: "rgba(0, 200, 180, 0.2)",
            borderColor: "rgba(0, 200, 180, 1)",
            tension: 0.4,
            borderWidth: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            // min: 0,
            //  max: 200,
            ticks: {
              // stepSize: 20,
              font: {
                size: 35,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 35,
              },
            },
          },
        },
      },
    });

    setTimeout(async () => {
      try {
        // console.log("chartRef1.current", chartRef1.current);
        const imageData1 = chartRef1.current.toDataURL();
        const imageData2 = chartRef2.current.toDataURL();
        const imageData3 = chartRef3.current.toDataURL();
        const imageData4 = chartRef4.current.toDataURL();
        const imageData5 = chartRef5.current.toDataURL();
        console.log("imageData1", imageData1);

        await generatePDF(imageData1, imageData2, imageData3, imageData4, imageData5);
      } catch (error) {
        console.error("Error capturing chart images:", error);
      }
    }, 1000);
  
    return () => {
      chart1.destroy();
      chart2.destroy();
      chart3.destroy();
      chart4.destroy();
      chart5.destroy();
    };
  
  }, [downloadPDF, session, isGenerating]);

  return (
    <>
      <canvas
        ref={chartRef1}
        width={700}
        height={500}
        style={{ display: "none" }}
      />
      <canvas
        ref={chartRef2}
        width={700}
        height={500}
        style={{ display: "none" }}
      />
      <canvas
        ref={chartRef3}
        width={700}
        height={500}
        style={{ display: "none" }}
      />
      <canvas
        ref={chartRef4}
        width={700}
        height={500}
        style={{ display: "none" }}
      />
      <canvas
        ref={chartRef5}
        width={700}
        height={500}
        style={{ display: "none" }}
      />
    </>
  );
};

export default ChartCapture;
